import { createModel } from '@rematch/core';
import { selectors } from './selector';
import { RootState } from '../../store';
import { CompositeAllergy } from '../../models/composite-allergy';
import {
    getCompositeAllergies,
    searchCompositeAllergies
} from '../../apis/emr-data';

export interface State {
    allIds: number[];
    byId: Dictionary<CompositeAllergy>;
    loading: Dictionary<boolean>;
    searchedTextHash: Dictionary<boolean>;
}

export const compositeAllergies = createModel<State>({
    state: {
        allIds: [],
        byId: {},
        loading: {},
        searchedTextHash: {}
    },
    reducers: {
        update: (
            state: State,
            data: Undefinable<MixType<CompositeAllergy>>,
            loadingKey?: string
        ): State => {
            const byId = {
                ...state.byId,
                ...storeByCompositeAllergyID(data)
            };
            return {
                ...state,
                allIds: Object.keys(byId)
                    .sort((a, b) => byId[a]!.description > byId[b]!.description ? 1 : -1)
                    .map(x => +x),
                byId: byId,
                ...loadingKey && {
                    loading: {
                        ...state.loading,
                        [loadingKey]: false
                    }
                }
            };
        },
        loading: (
            state: State,
            loadingKey: string,
            loading: boolean
        ): State => ({
            ...state,
            loading: {
                ...state.loading,
                [loadingKey]: loading
            }
        }),
        setSearched: (state: State, text: string): State => ({
            ...state,
            searchedTextHash: {
                ...state.searchedTextHash,
                [text]: true
            }
        })
    },
    effects: () => ({
        async getCompositeAllergiesAsync(ids: Undefinable<number>[], rootState: RootState) {
            const notExistedIds = selectors.getOtherCompositeAllergyIds(rootState.compositeAllergies, ids);
            if (notExistedIds.length > 0) {
                try {
                    const data = await getCompositeAllergies(notExistedIds);
                    this.update(data);
                } catch (e) {
                    window.console.log(e);
                }
            }
        },
        async searchCompositeAllergiesAsync(text: string, rootState: RootState) {
            const loading = selectors.getLoading(rootState.compositeAllergies, `searchCompositeAllergiesAsync---${text}`);
            if (!loading && !selectors.checkSearched(rootState.compositeAllergies, text)) {
                this.loading(`searchCompositeAllergiesAsync---${text}`, true);
                const data = await searchCompositeAllergies(text);
                this.update(data, `searchCompositeAllergiesAsync---${text}`);
                this.loading(`searchCompositeAllergiesAsync---${text}`, false);
                this.setSearched(text);
            }
        }
    })
});

export const storeByCompositeAllergyID = (data: Undefinable<MixType<CompositeAllergy>>): Dictionary<CompositeAllergy> => {
    if (data) {
        if (Array.isArray(data)) {
            return data.reduce(
                (prev, x) => {
                    prev[x.compositeAllergyID] = x;
                    return prev;
                },
                {} as Dictionary<CompositeAllergy>
            );
        } else {
            return {
                [data.compositeAllergyID]: data
            } as Dictionary<CompositeAllergy>;
        }
    } else {
        return {} as Dictionary<CompositeAllergy>;
    }
};
